import * as Sentry from "@sentry/core";
import {
  BUILTIN_PROP_LIBRARY_BIN_COMMITTED_KEY,
  BUILTIN_PROP_LIBRARY_BIN_STAGING_KEY,
} from "common/builtInPropRuntimeS3Keys";
import { getFileStorageBaseURL } from "common/constants/fileStorageBaseUrl";
import Flags from "common/constants/flags";
import { isJestTest, isOnNodeJS } from "common/utils/context";
import type { PropAsset } from "./PropAsset";
import { type PropLibrary, propLibraryBinaryConstants, propLibraryBinaryFlags } from "./propLibraryBinaryConstants";
import { loadPropLibraryGitBinaryBrowser } from "./propLibraryBrowserRuntime";
import { loadPropLibraryGitBinaryNode } from "./propLibraryNodeRuntime";
import { PropLibrarySource } from "./PropLibrarySource";
import { readNullTerminatedString } from "./propLibraryStringTable";

let cachedPropLibraryPromise: Promise<PropLibrary> | undefined;
let cachedPropLibrarySrc: PropLibrarySource | undefined;

export function decodePropLibraryBinary(bytes: Uint8Array): PropLibrary {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const decoder = new TextDecoder();

  const magic = decoder.decode(
    bytes.subarray(propLibraryBinaryConstants.headerMagicOffset, propLibraryBinaryConstants.headerVersionOffset),
  );
  if (magic !== propLibraryBinaryConstants.magic) {
    throw new Error(`Unexpected prop library magic: ${magic}`);
  }

  const version = view.getUint16(propLibraryBinaryConstants.headerVersionOffset, true);
  if (version !== propLibraryBinaryConstants.version && version !== 1 && version !== 2) {
    throw new Error(`Unsupported prop library version: ${version}`);
  }

  let offsetsLength = view.getUint32(propLibraryBinaryConstants.headerCountOffset, true);
  if (version === 1) {
    // In v1, headerCount stored the last prop ID (0-based), not the record count.
    offsetsLength += 1;
  }
  const stringTableOffset = view.getUint32(propLibraryBinaryConstants.headerStringTableOffset, true);
  const offsetsOffset = view.getUint32(propLibraryBinaryConstants.headerOffsetsOffset, true);
  const stringCache = new Map<number, string>();

  const propLibrary: Map<number, PropAsset> = new Map();

  for (let recordIndex = 0; recordIndex < offsetsLength; recordIndex += 1) {
    const recordOffset = view.getUint32(offsetsOffset + recordIndex * 4, true);
    if (recordOffset === 0) {
      // Only relevant for v1 parsing - v2 has explicit ids for each record.
      continue;
    }

    let id;
    let recordView: DataView;
    if (version === 1) {
      id = recordIndex;
      recordView = new DataView(bytes.buffer, recordOffset, propLibraryBinaryConstants.recordPayloadSize);
    } else {
      id = view.getUint32(recordOffset, true);
      recordView = new DataView(bytes.buffer, recordOffset + 4, propLibraryBinaryConstants.recordPayloadSize);
    }

    const nameOffset = recordView.getUint32(propLibraryBinaryConstants.recordNameOffset, true);
    const fbxHashOffset = recordView.getUint32(propLibraryBinaryConstants.recordFbxHashOffset, true);
    const smallThumbnailOffset = recordView.getUint32(propLibraryBinaryConstants.recordSmallThumbnailOffset, true);
    const largeThumbnailOffset = recordView.getUint32(propLibraryBinaryConstants.recordLargeThumbnailOffset, true);
    const flags = recordView.getUint32(propLibraryBinaryConstants.recordFlagsOffset, true);

    const minX = recordView.getFloat64(propLibraryBinaryConstants.recordMinXOffset, true);
    const minY = recordView.getFloat64(propLibraryBinaryConstants.recordMinYOffset, true);
    const minZ = recordView.getFloat64(propLibraryBinaryConstants.recordMinZOffset, true);
    const maxX = recordView.getFloat64(propLibraryBinaryConstants.recordMaxXOffset, true);
    const maxY = recordView.getFloat64(propLibraryBinaryConstants.recordMaxYOffset, true);
    const maxZ = recordView.getFloat64(propLibraryBinaryConstants.recordMaxZOffset, true);

    let s3Location: string | undefined;
    if (version >= 3) {
      const s3LocationBatchOffset = recordView.getUint32(propLibraryBinaryConstants.recordS3LocationBatchOffset, true);
      const s3LocationFileNameOffset2 = recordView.getUint32(
        propLibraryBinaryConstants.recordS3LocationFileNameOffset2,
        true,
      );
      const batchId = readNullTerminatedString(
        decoder,
        bytes,
        stringTableOffset + s3LocationBatchOffset,
        bytes.length,
        stringCache,
      );
      const fileName = readNullTerminatedString(
        decoder,
        bytes,
        stringTableOffset + s3LocationFileNameOffset2,
        bytes.length,
        stringCache,
      );
      if (batchId && fileName) {
        s3Location = `${batchId}/${fileName}`;
      }
    }

    const name = readNullTerminatedString(decoder, bytes, stringTableOffset + nameOffset, bytes.length, stringCache);
    const fbxHash = readNullTerminatedString(
      decoder,
      bytes,
      stringTableOffset + fbxHashOffset,
      bytes.length,
      stringCache,
    );

    if (name === undefined) {
      throw new Error(`Missing required prop library string data for prop id ${id}`);
    }

    const small = readNullTerminatedString(
      decoder,
      bytes,
      stringTableOffset + smallThumbnailOffset,
      bytes.length,
      stringCache,
    );
    const large = readNullTerminatedString(
      decoder,
      bytes,
      stringTableOffset + largeThumbnailOffset,
      bytes.length,
      stringCache,
    );

    let useExteriorPalette: boolean | undefined;
    if ((flags & propLibraryBinaryFlags.useExteriorPalettePresent) !== 0) {
      useExteriorPalette = (flags & propLibraryBinaryFlags.useExteriorPaletteValue) !== 0;
    }

    propLibrary.set(id, {
      name,
      fbxHash,
      hasSvg: (flags & propLibraryBinaryFlags.hasSvg) !== 0,
      usePropUv: (flags & propLibraryBinaryFlags.usePropUv) !== 0 ? true : undefined,
      useExteriorPalette,
      inReview: (flags & propLibraryBinaryFlags.inReview) !== 0 ? true : undefined,
      boundingBox: {
        minX,
        minY,
        minZ,
        maxX,
        maxY,
        maxZ,
      },
      s3Location,
      thumbnailsPNG: small || large ? { small, large } : undefined,
    });
  }

  return propLibrary;
}

async function loadPropLibraryBinaryAWS(propLibrarySource: PropLibrarySource): Promise<Uint8Array | undefined> {
  if (propLibrarySource === PropLibrarySource.AWS || propLibrarySource === PropLibrarySource.REVIEW) {
    const baseUrl = await getFileStorageBaseURL();
    if (!baseUrl) {
      throw new Error("Failed to load prop library binary from S3: Not able to load base URL.");
    }
    const key =
      propLibrarySource === PropLibrarySource.REVIEW
        ? BUILTIN_PROP_LIBRARY_BIN_STAGING_KEY
        : BUILTIN_PROP_LIBRARY_BIN_COMMITTED_KEY;
    // We want to always make sure that we have the latest version of the binary, so we use cache control.
    // With no-cache, the browser can still use a cached version, but will check if the server has a new version.
    const response = await fetch(`${baseUrl}${key}`, { cache: "no-cache" });
    if (response.ok) {
      return new Uint8Array(await response.arrayBuffer());
    }
    Sentry.captureException(
      new Error(`Failed to load prop library binary from S3: HTTP ${response.status}, falling back to static library`),
    );
  }
  return undefined;
}

/**
 * Tests should not be sensitive to changes in the AWS library or network availability, so they
 * always use the version of the library committed to git. Playwright drives a real browser, so the
 * opt-out can't be limited to Jest on Node: e2e runs against the `docker_test` S3 bucket, which
 * serves neither the prop library nor the CORS headers a browser fetch requires.
 */
function shouldUseGitPropLibraryForTests(): boolean {
  return (isOnNodeJS() && isJestTest()) || Flags.TEST_E2E_MODE;
}

async function loadPropLibraryBinary(propLibrarySource: PropLibrarySource): Promise<Uint8Array> {
  // AWS library is usually used in the browser, and can also be used on the server for server-side building/PDF generation.
  if (!shouldUseGitPropLibraryForTests()) {
    const awsBytes = await loadPropLibraryBinaryAWS(propLibrarySource);
    if (awsBytes) {
      return awsBytes;
    }
  }
  return isOnNodeJS() ? loadPropLibraryGitBinaryNode() : loadPropLibraryGitBinaryBrowser();
}

export async function loadPropLibrary(propLibrarySource: PropLibrarySource): Promise<PropLibrary> {
  if (cachedPropLibraryPromise && cachedPropLibrarySrc === propLibrarySource) {
    return cachedPropLibraryPromise;
  }
  cachedPropLibrarySrc = propLibrarySource;
  cachedPropLibraryPromise = (async () => {
    try {
      return decodePropLibraryBinary(await loadPropLibraryBinary(propLibrarySource));
    } catch (error: unknown) {
      cachedPropLibraryPromise = undefined;
      throw error;
    }
  })();
  return cachedPropLibraryPromise;
}
